import React, { useState } from "react";
import { BoardProps } from "./types/types";
import "../styles/CaesarСipherBoard.css";
import { useGame } from "../GameContext";
import { useTimerActions } from "../TimerContext";
import RenderAlphabet from "./render/RenderAlphabet";
import TextArea from "./render/TextArea";
import RenderBoardLogo from "./render/RenderBoardLogo";
import encryptWord from "./hooks/encryptWord";
import encryptWordType2 from "./hooks/encryptWordType2";
import shuffleArray from "./hooks/shuffleArray";

type AnswerStatus = "idle" | "correct" | "wrong";

const Board: React.FC<BoardProps> = ({ rounds, random, type }) => {
  const { isGameStarted, setMessage, setGuessesWords, setGameStarted } =
    useGame();

  const { startTimer, stopTimer, resetTimer } = useTimerActions();

  const createWordOrder = () => {
    const indexes = rounds.map((_, index) => index);
    return random ? shuffleArray(indexes) : indexes;
  };
  const [order, setOrder] = useState(createWordOrder);

  const [isGameFinished, setIsGameFinished] = useState(false);
  const [roundIndex, setRoundIndex] = useState(0);
  const [wordIndex, setWordIndex] = useState(0);
  const [answerValue, setAnswerValue] = useState("");
  const [answerStatus, setAnswerStatus] = useState<AnswerStatus>("idle");
  const [blockUserInput, setBlockUserInput] = useState(true);
  const [shift, setShift] = useState<number>(0);

  const normalizeWord = (text: string) =>
    text.replace(/[|\s]/g, "").toUpperCase();

  const currentRound = rounds[order[roundIndex]];
  const words = currentRound.words.split(" ");

  const currentWord = normalizeWord(words[wordIndex]);
  let cipherWord;

  if (type === "1") {
    cipherWord = encryptWord(currentWord, currentRound.key!);
  } else {
    cipherWord = encryptWordType2(currentWord);
  }
  const isLastWord = wordIndex === words.length - 1;
  const isLastRound = roundIndex === rounds.length - 1;

  const resetAnswer = () => {
    setAnswerValue("");
    setAnswerStatus("idle");
  };

  const startRound = () => {
    setWordIndex(0);
    setShift(0);
    resetAnswer();

    setBlockUserInput(false);
    setMessage("Разгадай зашифрованное послание");

    setGuessesWords([]);
    startTimer();
  };

  const startGame = () => {
    resetTimer();

    setRoundIndex(0);
    setWordIndex(0);
    setIsGameFinished(false);

    setGuessesWords([]);

    setGameStarted(true);

    startRound();
  };

  const nextWord = () => {
    setWordIndex((prev) => prev + 1);
    resetAnswer();
  };

  const finishRound = () => {
    stopTimer();
    setBlockUserInput(true);
  };

  const nextRound = () => {
    setRoundIndex((prev) => prev + 1);
    startRound();
  };

  const checkAnswer = (value: string) => {
    if (blockUserInput || !isGameStarted || isGameFinished) {
      return;
    }

    const answer = normalizeWord(value);

    // Пустой ответ
    if (!answer) {
      setMessage("Введите слово");
      setAnswerStatus("wrong");
      return;
    }

    // Неверный ответ
    if (answer !== currentWord) {
      setMessage("Ответ неверный");
      setAnswerStatus("wrong");
      return;
    }

    // Правильный ответ
    setAnswerStatus("correct");

    setGuessesWords((prev) => [...prev, currentWord]);

    // Есть ещё слова в этом раунде
    if (!isLastWord) {
      nextWord();
      return;
    }

    // Раунд закончился
    finishRound();

    // Закончилась вся игра
    if (isLastRound) {
      setIsGameFinished(true);
      setMessage("Молодец, нажми играть ещё");
      return;
    }

    // Есть следующий раунд
    setMessage("Нажми далее");
  };

  const handleNext = () => {
    if (isGameFinished) {
      startGame();
      return;
    }

    nextRound();
  };

  const handleStart = () => {
    startGame();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswerValue(e.target.value);

    if (answerStatus === "wrong") {
      setAnswerStatus("idle");
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.repeat) {
      return;
    }

    e.preventDefault();

    checkAnswer(answerValue);

    e.currentTarget.blur();
  };

  return (
    <div className="CaesarСipher-game-wrapper">
      <RenderAlphabet
        shift={shift}
        setShift={setShift}
        blockUserInput={blockUserInput}
        type={type}
      />

      <div className="CaesarСipher-board-wrapper">
        <div className="CaesarСipher-board-content">
          <RenderBoardLogo
            isGameStarted={isGameStarted}
            isGameFinished={isGameFinished}
            onNext={handleNext}
            onStart={handleStart}
          />
          {/* Игровое поле */}
          <div className="CaesarСipher-board">
            {/* Поле с шифром и шифром */}
            <div className="CaesarСipher-key-cipher-content">
              {type === "1" && (
                <div className="CaesarСipher-key-content">
                  <img
                    className="CaesarСipher-key-img"
                    src="/img/caesarcipher/key.svg"
                    alt="key"
                  />

                  <div className="CaesarСipher-key-number">{shift}</div>
                </div>
              )}

              {isGameStarted && !isGameFinished && (
                <div
                  className={`CaesarСipher-cipher ${
                    cipherWord.length > 12 ? "CaesarСipher-cipher--small" : ""
                  }`}
                >
                  {cipherWord.map((number, index) => (
                    <span key={index}>{number}</span>
                  ))}
                </div>
              )}
            </div>
            {/* Поле ввода и кнопки */}
            <div className="CaesarСipher-button-input-board">
              <div className="CaesarСipher-subtitle">Введи ответ:</div>

              <input
                className={`CaesarСipher-input CaesarСipher-${answerStatus}`}
                disabled={blockUserInput}
                value={answerValue}
                onFocus={() => setAnswerStatus("idle")}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
              />

              {!blockUserInput && (
                <button
                  title="Проверить ответ"
                  className="CaesarСipher-buttonCheck"
                  onClick={() => checkAnswer(answerValue)}
                >
                  Проверить
                </button>
              )}
            </div>
          </div>
        </div>

        <TextArea />
      </div>
    </div>
  );
};

export default Board;
